import React, { useState, useEffect, useRef } from 'react';
import { CSVLink } from 'react-csv';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { exportToPDF, printTableFromHtml, generateCSVData } from '../../services/exportService';

const CardReportView = () => {
  const [typeFilter, setTypeFilter] = useState('');
  const [titleFilter, setTitleFilter] = useState('');
  const [reportData, setReportData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [summary, setSummary] = useState({
    listenedCount: 0,
    totalDuration: 0,
    purchasedCount: 0,
  });

  const tableRef = useRef(null);

  useEffect(() => {
    fetchReportData();
  }, []);

  useEffect(() => {
    applyFilter();
  }, [reportData, typeFilter, titleFilter]);

  const fetchReportData = () => {
    // Dummy data for example
    const dummyData = [
      {
        title: 'Panchatantra Story',
        type: 'Story',
        listenedDuration: 25,
        isPurchased: true,
        listenCount: 3,
        lastListened: '2025-05-12',
        chapters: [
          { name: 'Monkey and Crocodile', duration: 10 },
          { name: 'Clever Rabbit', duration: 15 },
        ],
      },
      {
        title: 'ABC Song',
        type: 'Song',
        listenedDuration: 10,
        isPurchased: false,
        listenCount: 1,
        lastListened: '2025-05-11',
        chapters: [
          { name: 'Alphabet Song', duration: 10 },
        ],
      },
    ];
    setReportData(dummyData);
  };

  const applyFilter = () => {
    let filtered = [...reportData];

    if (typeFilter.trim()) {
      filtered = filtered.filter(d =>
        d.type.toLowerCase().includes(typeFilter.trim().toLowerCase())
      );
    }

    if (titleFilter.trim()) {
      filtered = filtered.filter(d =>
        d.title.toLowerCase().includes(titleFilter.trim().toLowerCase())
      );
    }

    setFilteredData(filtered);

    const totalDuration = filtered.reduce((sum, item) => sum + item.listenedDuration, 0);
    const purchasedCount = filtered.filter(item => item.isPurchased).length;
    setSummary({
      listenedCount: filtered.length,
      totalDuration,
      purchasedCount,
    });
  };

  const toggleExpand = (index) => {
    setExpandedIndex(prev => (prev === index ? null : index));
  };

  const handleExportPDF = () => {
    const columns = ['#', 'Card Title', 'Type', 'Duration', 'Listen Count'];
    const data = filteredData.map((item, i) => [
      i + 1,
      item.title,
      item.type,
      `${item.listenedDuration} mins`,
      item.listenCount,
    ]);
  
    const summaryData = [
      ['Cards Listened', summary.listenedCount],
      ['Total Listening Time', `${summary.totalDuration} mins (${(summary.totalDuration / 60).toFixed(2)} hrs)`],
      ['Total Listening Card', filteredData.map(d => d.title).join(', ') || 'N/A']
    ];
  
    exportToPDF('Card Usage Report', columns, data, 'CardUsageReport.pdf', summaryData);
  };
    const handlePrint = () => {
    if (tableRef.current) {
      printTableFromHtml(tableRef.current.innerHTML, 'Card Usage Report');
    }
  };

  return (
    <div className="container mt-4">
      <div className="card p-3">

        {/* Filters */}
        <div className="row mb-3 gx-2">
          <div className="col-md-6">
            <input
              type="text"
              className="form-control"
              placeholder="Filter by Card Title"
              value={titleFilter}
              onChange={e => setTitleFilter(e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <input
              type="text"
              className="form-control"
              placeholder="Filter by Type"
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value)}
            />
          </div>
        </div>

        {/* Header & Export Buttons */}
        <div className="card-header d-flex justify-content-between align-items-center mb-3 px-0 border-0">
          <h3 className="mb-0">Card Report</h3>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-danger" onClick={handleExportPDF}>Export PDF</button>
            <CSVLink
              filename="CardUsageReport.csv"
              className="btn btn-outline-info"
              data={generateCSVData(
                ['#', 'Card Title', 'Type', 'Duration', 'Listen Count'],
                filteredData.map((item, i) => [
                  i + 1,
                  item.title,
                  item.type,
                  `${item.listenedDuration} mins`,
                  item.listenCount,
                ])
              )}
            >
              Export CSV
            </CSVLink>
            <button className="btn btn-outline-dark" onClick={handlePrint}>Print</button>
          </div>
        </div>


        {/* Summary */}
        <div ref={tableRef}>
        <div className="row mb-4">
          <div className="col">
            <div className="p-3 bg-light border rounded text-center">
              <div className="fw-bold">Cards Listened</div>
              <div>{summary.listenedCount}</div>
            </div>
          </div>
          <div className="col">
            <div className="p-3 bg-light border rounded text-center">
              <div className="fw-bold">Total Listening Time</div>
              <div>{summary.totalDuration} mins ({(summary.totalDuration / 60).toFixed(2)} hrs)</div>
            </div>
          </div>
          <div className="col">
            <div className="p-3 bg-light border rounded text-center">
              <div className="fw-bold">Total Listening Card</div>
              <div>ABC Song</div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="table-responsive">
          <table className="table table-bordered mb-0">
            <thead className="table-light">
              <tr>
                <th className="text-center">#</th>
                <th className="text-center">Card Title</th>
                <th className="text-center">Type</th>
                <th className="text-center">Duration</th>
                <th className="text-center">Listen Count</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.length > 0 ? (
                filteredData.map((item, i) => (
                  <React.Fragment key={i}>
                    <tr>
                      <td className="text-center">{i + 1}</td>
                      <td
                        style={{ cursor: 'pointer', color: '#007bff' }}
                        onClick={() => toggleExpand(i)}
                      >
                        <div className="d-inline-flex align-items-center gap-2">
                          {item.title}
                          {expandedIndex === i ? <FaChevronUp size={14} /> : <FaChevronDown size={14} />}
                        </div>
                      </td>
                      <td className="text-center">{item.type}</td>
                      <td className="text-center">{item.listenedDuration} mins</td>
                      <td className="text-center">{item.listenCount}</td>
                    </tr>

                    {expandedIndex === i && (
                      <tr>
                        <td colSpan="5" style={{ padding: '20px' }}>
                          {item.chapters?.length > 0 ? (
                            <div
                              style={{
                                backgroundColor: '#ffffff',
                                borderRadius: '10px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                padding: '15px 25px',
                                maxHeight: '300px',
                                overflowY: 'auto',
                              }}
                            >
                              <h5 style={{ marginBottom: '10px', fontWeight: '700', color: '#444' }}>
                                Chapters
                              </h5>
                              {item.chapters.map((chapter, idx) => (
                                <div
                                  key={idx}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '8px 6px',
                                    borderBottom: idx !== item.chapters.length - 1 ? '1px solid #eee' : 'none',
                                    transition: 'background-color 0.2s ease',
                                    cursor: 'default',
                                  }}
                                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f9f9f9')}
                                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                >
                                  <div style={{ flexBasis: '30px', fontWeight: '600', color: '#555' }}>
                                    {idx + 1}
                                  </div>
                                  <div style={{ flex: 1, fontWeight: '600', color: '#222' }}>
                                    {chapter.name || 'N/A'}
                                  </div>
                                  <div
                                    style={{
                                      width: '140px',
                                      textAlign: 'right',
                                      color: '#666',
                                      fontWeight: '500',
                                    }}
                                  >
                                    {(Number(chapter.duration) || 0)} mins
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-muted p-2">No chapters found.</div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center py-3">
                    No data found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        </div>
      </div>
    </div>
  );
};

export default CardReportView;
